import { useLayoutEffect, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';

import { OrbitControls, useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import { PosData, SkateboardStore } from 'models/RunModel/Skateboard';
import useAutorun from 'hooks/useAutorun';
import _ from 'lodash';

interface SkateboardProps {
  obs: SkateboardStore;
  controlsRef?: React.RefObject<OrbitControlsImpl | null>;
}

/**
   Three-JS Coordinate-System:
   - Forward: +X
   - Right:   +Z
   - Up:      +Y

   Sensor Coordinate-System:
   - Forward: +X
   - Right:   -Y
   - Up:      +Z
*/

// TIM TRICK: 70_1B_77_91_6D_35_2022_10_27_11_42_23
const Skateboard = (props: SkateboardProps) => {
  const { obs, controlsRef } = props;

  const posData = useRef<PosData | undefined>(undefined);

  // we can't use `observer` here to listen to `props.obs.frame` directly
  //   because this Component is used by a `react-three/fiber` component
  useAutorun(() => {
    posData.current = obs.posData;
  });

  const [positions, setPositions] = useState<Vector3[]>([]);
  useAutorun(() => {
    setPositions(obs.allPosData.map((d) => d.loc));
  });

  const ctrls = useRef<OrbitControlsImpl | null>(null);
  const skateboard = useRef<Group>(null);
  
  // Use the provided controlsRef or our own internal ref
  const effectiveControlsRef = controlsRef || ctrls;
  /** 6 is centered, 4 is axis aligned */
  const boardScene = useGLTF(
    '/assets/3d/skateboard_uvd_spinnax_real_sized.glb',
  );
  const board = boardScene.scene;

  useLayoutEffect(() => {
    board.traverse((obj) => {
      obj.receiveShadow = true;
      obj.castShadow = true;
    });
  }, [board]);
  const curFrame = useRef(0);
  const isRunning = useRef(false);

  useAutorun(() => {
    const newFrame = obs.frame;
    if (!isRunning.current) {
      curFrame.current = newFrame;
    }
  });

  useFrame((state, _delta) => {
    if (!skateboard.current || !effectiveControlsRef.current || !posData.current) return;
    
    // NUR die State-Machine prüfen, nicht state.clock.running!
    const isAnimationRunning = obs.animationState === "playing" && obs.positionState !== "end";
    isRunning.current = isAnimationRunning;
    
    // KEINE eigene Frame-Animation! Das macht jetzt ViewRun/FrameSync
    // if (isAnimationRunning) {
    //   curFrame.current += (_delta * 1000) / obs.interval;
    //   obs.frame = _.round(curFrame.current, 0);
    //   if (obs.frame === obs.frameNum) state.clock.stop();
    // }
    
    const step = posData.current;
    const targetPos = step.loc;
    const targetRot = step.rot;

    const delta = targetPos.manhattanDistanceTo(skateboard.current.position);
    if (delta < 1e-5) {
      if (!state.clock.running) return; // don't do anything more
    }
    /* let alpha = 0.1; */
    let alpha = 0.2;

    // TODO: try AnimationMixer / KeyFrameTracks https://discoverthreejs.com/book/first-steps/animation-system/
    /* const step = frameData[(frameref as MutableRefObject<number>).current]; */

    const newPos = skateboard.current.position.lerp(targetPos, alpha);
    (effectiveControlsRef.current as OrbitControlsImpl).target.set(
      newPos.x,
      newPos.y,
      newPos.z
    );
    skateboard.current.quaternion.slerp(targetRot, alpha);
  });

  return (
    <group>
      {/* Trajektorie-Linie entfernt */}
      <group ref={skateboard as React.Ref<Group>}>
        <primitive object={board} op />
        <OrbitControls
          maxPolarAngle={Math.PI / 2}
          enabled
          minDistance={0}
          maxDistance={5}
          ref={effectiveControlsRef}
        />
      </group>
    </group>
  );
};

export default Skateboard;
